import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

interface Product {
  id: string;
  title: string;
  price: number;
  originalPrice: number;
  image: string;
  seller: string;
  verified: boolean;
  rating: number;
  reviews: number;
  category: string;
  badge: string;
  stock: number;
  warehouse: string;
  dispatchHours: number;
  deliveryDays: string;
  commission: string;
}

function useCountdown(hours: number) {
  const getSecondsLeft = () => {
    const now = new Date();
    const target = new Date();
    target.setHours(target.getHours() + hours, 0, 0, 0);
    return Math.max(0, Math.floor((target.getTime() - now.getTime()) / 1000));
  };
  const [seconds, setSeconds] = useState(getSecondsLeft);
  useEffect(() => {
    const t = setInterval(() => setSeconds(getSecondsLeft()), 1000);
    return () => clearInterval(t);
  }, [hours]);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function USAProductCard({ product }: { product: Product }) {
  const [hovered, setHovered] = useState(false);
  const countdown = useCountdown(product.dispatchHours);
  const discount = Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100);
  const isSameDay = product.dispatchHours <= 4;

  return (
    <Link
      to={`/product/${product.id}`}
      className="group bg-white rounded-2xl border overflow-hidden transition-all duration-200 cursor-pointer flex flex-col"
      style={{ borderColor: hovered ? "#DAA520" : "#f3f4f6" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image */}
      <div className="relative w-full h-52 overflow-hidden bg-gray-50 flex-shrink-0">
        <img
          src={product.image}
          alt={product.title}
          className={`w-full h-full object-cover object-top transition-transform duration-300 ${hovered ? "scale-105" : "scale-100"}`}
        />
        {/* Badges top-left */}
        <div className="absolute top-3 left-3 flex flex-col gap-1">
          {product.badge && (
            <span
              className="inline-block px-2 py-0.5 text-xs font-bold rounded-full"
              style={{ background: "linear-gradient(135deg, #B8860B, #DAA520)", color: "#fff" }}
            >
              {product.badge}
            </span>
          )}
          {discount > 0 && (
            <span className="inline-block px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
              -{discount}% OFF
            </span>
          )}
        </div>
        {/* Same-day badge top-right */}
        {isSameDay && (
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-full" style={{ background: "#16a34a", color: "#fff" }}>
              <i className="ri-flashlight-line" /> Same Day
            </span>
          </div>
        )}
        {/* Warehouse bottom */}
        <div className="absolute bottom-3 left-3">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-black/60 text-white text-xs rounded-full">
            <i className="ri-map-pin-line text-xs" /> {product.warehouse}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        {/* Seller */}
        <p className="text-xs text-gray-400 mb-1 flex items-center gap-1 truncate">
          {product.verified && <i className="ri-shield-check-fill text-green-500 flex-shrink-0" />}
          <span className="truncate">{product.seller}</span>
        </p>
        {/* Title */}
        <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 mb-2 flex-1">
          {product.title}
        </p>
        {/* Rating */}
        <div className="flex items-center gap-1.5 mb-3">
          <div className="flex items-center gap-0.5">
            {[1,2,3,4,5].map((s) => (
              <i key={s} className={s <= Math.round(product.rating) ? "ri-star-fill text-xs" : "ri-star-line text-xs"} style={{ color: "#DAA520" }} />
            ))}
          </div>
          <span className="text-xs text-gray-400">({product.reviews.toLocaleString()})</span>
        </div>
        {/* Price */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xl font-extrabold text-gray-900">${product.price}</span>
          <span className="text-sm text-gray-400 line-through">${product.originalPrice}</span>
          <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(184,134,11,0.12)", color: "#B8860B" }}>
            {product.commission} comm.
          </span>
        </div>
        {/* Dispatch countdown */}
        <div
          className="rounded-xl px-3 py-2 text-center"
          style={{ background: isSameDay ? "rgba(22,163,74,0.07)" : "rgba(184,134,11,0.07)" }}
        >
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-xs font-bold" style={{ color: isSameDay ? "#16a34a" : "#B8860B" }}>
              <i className="ri-truck-line mr-1" />
              {isSameDay ? "Same-Day Dispatch" : "Fast Dispatch"}
            </span>
            <span className="text-xs font-mono font-bold" style={{ color: isSameDay ? "#15803d" : "#92400e" }}>
              {countdown}
            </span>
          </div>
          <p className="text-xs text-gray-400">Delivers in {product.deliveryDays} business days · {product.warehouse}</p>
        </div>
      </div>
    </Link>
  );
}
